import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, X, Video, FileText, Loader2 } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { RichTextEditor } from "./RichTextEditor";

type Lesson = Database["public"]["Tables"]["lessons"]["Row"];
type LessonType = Database["public"]["Enums"]["lesson_type"];

interface LessonEditorProps {
  lesson: Lesson;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedLesson: Lesson) => void;
}

export const LessonEditor = ({ lesson, isOpen, onClose, onSave }: LessonEditorProps) => {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: lesson.title,
    description: lesson.description || "",
    content: lesson.content || "",
    video_url: lesson.video_url || "",
    lesson_type: lesson.lesson_type as LessonType,
    duration_minutes: lesson.duration_minutes || 0,
    is_preview: lesson.is_preview || false,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("lessons")
        .update({
          title: formData.title,
          description: formData.description || null,
          content: formData.content || null,
          video_url: formData.video_url || null,
          lesson_type: formData.lesson_type,
          duration_minutes: formData.duration_minutes || null,
          is_preview: formData.is_preview,
          updated_at: new Date().toISOString(),
        })
        .eq("id", lesson.id)
        .select()
        .single();

      if (error) throw error;

      toast.success(t("courseEditor.lessonSaved"));
      onSave(data);
      onClose();
    } catch (error) {
      console.error("Error saving lesson:", error);
      toast.error(t("courseEditor.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t("courseEditor.editLesson")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t("courseEditor.lessonTitle")}</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t("courseEditor.lessonTitlePlaceholder")}
            />
          </div>

          {/* Lesson Type */}
          <div className="space-y-2">
            <Label>{t("courseEditor.lessonType")}</Label>
            <Select
              value={formData.lesson_type || "video"}
              onValueChange={(value: LessonType) => setFormData({ ...formData, lesson_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="video">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4" />
                    {t("courseEditor.typeVideo")}
                  </div>
                </SelectItem>
                <SelectItem value="text">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {t("courseEditor.typeText")}
                  </div>
                </SelectItem>
                <SelectItem value="quiz">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {t("courseEditor.typeQuiz")}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Video URL (if video type) */}
          {formData.lesson_type === "video" && (
            <div className="space-y-2">
              <Label htmlFor="video_url">{t("courseEditor.videoUrl")}</Label>
              <div className="flex gap-2">
                <Video className="h-5 w-5 text-muted-foreground mt-2" />
                <Input
                  id="video_url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/embed/..."
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t("courseEditor.videoUrlHint")}
              </p>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t("courseEditor.description")}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t("courseEditor.descriptionPlaceholder")}
              rows={3}
            />
          </div>

          {/* Content (Rich text editor) */}
          <div className="space-y-2">
            <Label>{t("courseEditor.content")}</Label>
            <RichTextEditor
              value={formData.content}
              onChange={(content) => setFormData({ ...formData, content })}
              placeholder={t("courseEditor.contentPlaceholder")}
            />
          </div>

          {/* Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="duration">{t("courseEditor.duration")}</Label>
              <Input
                id="duration"
                type="number"
                min="0"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("courseEditor.freePreview")}</Label>
              <Select
                value={formData.is_preview ? "yes" : "no"}
                onValueChange={(value) => setFormData({ ...formData, is_preview: value === "yes" })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">{t("courseEditor.yes")}</SelectItem>
                  <SelectItem value="no">{t("courseEditor.no")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              <X className="h-4 w-4 mr-2" />
              {t("courseEditor.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={saving || !formData.title.trim()}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {t("courseEditor.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
